import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { assignmentKeys } from '../queries/keys';
import { assignmentPaths } from '../queries/paths';

// TSS vite/03 — server state for `project-teams`: which team reaches which
// project, and how far.
//
// Every write invalidates the project's assignments, because every one of them
// changes the scope sentence the backend hands back — and that sentence is the
// only thing on screen that says what the assignment actually grants.

export function useProjectAssignments(projectId) {
    return useQuery({
        queryKey: assignmentKeys.byProject(projectId),
        queryFn: ({ signal }) => api
            .get(assignmentPaths.list(), { params: { project_id: projectId, limit: 100 }, signal })
            .then(r => r.data),
        enabled: !!projectId,
    });
}

// Read only, and it runs the SAME axis conditions as the scope resolver — so
// what it shows is what the system will really grant. `total` is exact; the
// task list is a sample and says when it was cut.
export function useAssignmentPreview(id, { enabled } = {}) {
    return useQuery({
        queryKey: assignmentKeys.preview(id),
        queryFn: ({ signal }) => api.get(assignmentPaths.preview(id), { signal }).then(r => r.data),
        enabled: !!id && !!enabled,
    });
}

// POST /admin/project-teams — assigning is born WITHOUT lists on either axis,
// which means reaching the whole project. It narrows afterwards, one axis at a
// time. Re-assigning a team that had been removed answers 200 instead of 201
// and brings back the axes it had.
export function useAssignTeam(projectId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (teamId) => api
            .post(assignmentPaths.list(), { project_id: Number(projectId), team_id: Number(teamId) })
            .then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: assignmentKeys.byProject(projectId) }),
    });
}

// Soft removal: it stops granting scope immediately and the row survives,
// because its two lists are the record of what that team could touch while it
// worked.
export function useUnassignTeam(projectId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.delete(assignmentPaths.detail(id)).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: assignmentKeys.byProject(projectId) }),
    });
}

// The four axis writes. A repository from another project answers 422; adding
// the same one twice is idempotent.
export function useScopeAxis(projectId) {
    const qc = useQueryClient();
    const invalidate = (id) => {
        qc.invalidateQueries({ queryKey: assignmentKeys.byProject(projectId) });
        qc.invalidateQueries({ queryKey: assignmentKeys.preview(id) });
    };

    const addRepository = useMutation({
        mutationFn: ({ id, repositoryId }) => api
            .post(assignmentPaths.repositories(id), { repository_id: Number(repositoryId) })
            .then(r => r.data),
        onSuccess: (_data, variables) => invalidate(variables.id),
    });

    const removeRepository = useMutation({
        mutationFn: ({ id, repositoryId }) => api
            .delete(assignmentPaths.repository(id, repositoryId)).then(r => r.data),
        onSuccess: (_data, variables) => invalidate(variables.id),
    });

    const addTier = useMutation({
        mutationFn: ({ id, tierId }) => api
            .post(assignmentPaths.tiers(id), { tier_id: Number(tierId) }).then(r => r.data),
        onSuccess: (_data, variables) => invalidate(variables.id),
    });

    const removeTier = useMutation({
        mutationFn: ({ id, tierId }) => api
            .delete(assignmentPaths.tier(id, tierId)).then(r => r.data),
        onSuccess: (_data, variables) => invalidate(variables.id),
    });

    return { addRepository, removeRepository, addTier, removeTier };
}
